import { EntityType } from '~/enums/EntityType';
import { SearchQuery } from '~/features/advanced-search/types';
import type { TargetEntity } from '~/features/x-ray/types/entity';
import type { ModelResult } from '~/features/x-ray/types/model';
import { sylvanus } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';

import {
  PagePromptModel,
  PromptModel,
  PromptModelStatus,
} from '../../types/sylvanus/model';

interface Request {
  pageIndex: number;
  pageSize: number;
  //TODO: Remove this once the API type is updated
  status: Nullable<PromptModelStatus | 'republished'>;
  sortBy?: {
    id: string;
    desc: boolean;
  };
}

interface Response {
  results: ModelResult[];
  totalCount: number;
}

export const getPromptModelResults = async ({
  pageIndex,
  pageSize,
  status,
  sortBy,
}: Request): Promise<Response> => {
  const response: PagePromptModel = await sylvanus.get('/model', {
    params: {
      include_latest_version: true,
      page: pageIndex,
      size: pageSize,
      status,
      sort_by: sortBy?.id,
      reverse: sortBy?.desc,
    },
  });

  return {
    results: response.items.map(mapModelResult()),
    totalCount: response.total ?? 0,
  };
};

const mapModelResult =
  () =>
  (model: PromptModel): ModelResult => {
    const result: ModelResult = {
      currentOptimize: null,
      currentPublishStatus: model.current_publish?.status ?? null,
      id: model.id,
      latestVersion: 1,
      libraryModelId: model.library_model_info?.library_model_id,
      scope: [],
      targetEntity: model.target_entity as TargetEntity,
      versions: [],
    };

    const versions = model.version_numbers ?? [];
    const latestVersion = versions.slice(-1)[0];
    const latestModelVersion = model.versions?.[latestVersion];

    if (latestModelVersion) {
      result.currentOptimize =
        model.current_optimize?.version_number === latestVersion
          ? {
              state: model.current_optimize.state ?? null,
              version: model.current_optimize.version_number,
            }
          : null;
      result.latestVersion = latestVersion;
      result.modifiedBy = Number(latestModelVersion.created_by_user_id);
      //l10n-ignore: API parsing no formatting required
      result.modifiedDate = new Date(model.datetime_updated);
      result.versions = versions;
      if (latestModelVersion.config?.scope) {
        result.scope = latestModelVersion.config.scope.data.attributes
          .query as SearchQuery;
      }
      if (latestModelVersion.config?.prompt_model_type === 'table_extraction') {
        result.targetEntityDetails = {
          id: model.target_entity.id,
          label: latestModelVersion.config.table_spec.name,
          type: EntityType.DocumentAttachedData,
        };
      }
    }

    return result;
  };
